import { useCallback, useEffect, useState } from "react";
import { api, asArray, formatApiError } from "@/lib/api";
import { RefreshCw, Smile, Star } from "lucide-react";

// GET /desk/csat -- the desk's own view of the surveys Task 6 taught the
// portal to answer. The one property this page must never hide: an average
// of 5.0 from two responses out of ninety surveys is not a good score, so
// the response rate (and the raw "N of M" it is built from) sits right next
// to the average, not behind a second click or a tooltip.
//
// The server is the gate, not this page: a client-portal account is refused
// this route by authz.client_scope_guard's default deny before it ever
// reaches csat.desk_csat_report (the route is in neither authz._PUBLIC nor
// authz.PORTAL_ALLOW). The `roles` filter on this page's nav entry
// (Layout.jsx) only removes a door a client could not open anyway.

const DAY_OPTIONS = [
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 180, label: "Last 180 days" },
  { value: 365, label: "Last 12 months" },
];

function pct(n) {
  return n == null ? "—" : `${Math.round(n * 100)}%`;
}

function fmtAvg(n) {
  return n == null ? "—" : n.toFixed(2);
}

export default function CsatReport() {
  const [days, setDays] = useState(90);
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(asArray(r.data))).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setErr("");
    const q = new URLSearchParams({ days: String(days) });
    if (clientId) q.set("client_id", clientId);
    api.get(`/desk/csat?${q.toString()}`)
      .then((r) => setReport(r.data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [days, clientId]);
  useEffect(() => { load(); }, [load]);

  const issued = report?.issued ?? 0;
  const responses = report?.responses ?? 0;
  const hasAnyResponses = responses > 0;
  const distEntries = [1, 2, 3, 4, 5].map((n) => ({
    score: n, count: report?.distribution?.[String(n)] ?? 0,
  }));
  const maxCount = Math.max(1, ...distEntries.map((d) => d.count));

  return (
    <div className="space-y-4" data-testid="csat-report-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Service Desk · Feedback</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">CSAT Report</h1>
          <div className="text-xs text-zinc-500 mt-1">
            The average tells you how happy responders were. The response rate tells you how many of them there were.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            data-testid="csat-days-select"
            className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-zinc-300">
            {DAY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}
            data-testid="csat-client-select"
            className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-zinc-300">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
          </select>
          <button onClick={load} data-testid="csat-report-refresh"
            className="border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900 flex items-center gap-1.5">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {err && <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm" data-testid="csat-report-error">{err}</div>}
      {loading && !err && <div className="text-zinc-600 text-sm">Loading…</div>}

      {!loading && !err && report && (
        <>
          {/* The two headline numbers, side by side, same visual weight --
              neither one is allowed to read as the whole story alone. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4" data-testid="csat-average-card">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-1.5">
                <Smile size={12} /> Average score
              </div>
              {hasAnyResponses ? (
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-3xl font-display text-zinc-100" data-testid="csat-average-value">
                    {fmtAvg(report.average)}
                  </span>
                  <span className="text-zinc-500 text-sm">/ 5</span>
                </div>
              ) : (
                <div className="text-lg text-zinc-500 mt-2" data-testid="csat-average-empty">
                  No responses yet in this window — not a 0.0 score, simply unmeasured.
                </div>
              )}
            </div>
            <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4" data-testid="csat-response-rate-card">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Response rate</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-3xl font-display text-zinc-100" data-testid="csat-response-rate-value">
                  {pct(report.response_rate)}
                </span>
                <span className="text-zinc-500 text-sm" data-testid="csat-response-rate-detail">
                  {responses} of {issued} survey{issued === 1 ? "" : "s"} answered
                </span>
              </div>
              {issued === 0 && (
                <div className="text-xs text-zinc-600 mt-1">No surveys were issued in this window.</div>
              )}
            </div>
          </div>

          {/* Distribution -- all five bars, every run, so a score nobody
              gave this window draws as an honest empty bar, not a missing
              one. */}
          <Section title="Score distribution" testid="csat-distribution-section">
            <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-2">
              {distEntries.map((d) => (
                <div key={d.score} className="flex items-center gap-3" data-testid={`csat-dist-row-${d.score}`}>
                  <div className="w-10 text-xs text-zinc-400 flex items-center gap-1 justify-end">
                    {d.score} <Star size={10} className="text-zinc-600" />
                  </div>
                  <div className="flex-1 bg-zinc-900 rounded h-3 overflow-hidden">
                    <div className="bg-blue-600/70 h-3 rounded"
                      style={{ width: `${(d.count / maxCount) * 100}%` }} />
                  </div>
                  <div className="w-8 text-xs text-zinc-400 font-mono text-right" data-testid={`csat-dist-count-${d.score}`}>
                    {d.count}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="By technician" testid="csat-by-technician-section">
            {!asArray(report.by_technician).length && (
              <EmptyRow msg="No answered surveys map to a technician in this window." />
            )}
            {!!asArray(report.by_technician).length && (
              <Table
                head={["Technician", "Responses", "Average"]}
                rows={report.by_technician.map((t, idx) => ({
                  key: t.technician_id || `unassigned-${idx}`,
                  testid: `csat-tech-${t.technician_id || "unassigned"}`,
                  cells: [t.name, t.responses, fmtAvg(t.average)],
                }))}
              />
            )}
          </Section>

          <Section title="By client" testid="csat-by-client-section">
            {!asArray(report.by_client).length && (
              <EmptyRow msg="No answered surveys in this window." />
            )}
            {!!asArray(report.by_client).length && (
              <Table
                head={["Client", "Responses", "Average"]}
                rows={report.by_client.map((c, idx) => ({
                  key: c.client_id || `unassigned-${idx}`,
                  testid: `csat-client-${c.client_id || "unassigned"}`,
                  cells: [c.name, c.responses, fmtAvg(c.average)],
                }))}
              />
            )}
          </Section>

          {/* The portal asks "Anything you'd like to add (optional)" and,
              until this, nothing at the desk could ever read the answer.
              Rendered as PLAIN TEXT ONLY -- `{c.comment}` is a React text
              child, not dangerouslySetInnerHTML, so JSX escapes it on its
              own; there is no markdown/HTML rendering path here at all,
              which is deliberate for customer-authored free text in a staff
              console. The score and ticket number ride beside every
              comment: a comment with neither is an anecdote nobody could
              act on. */}
          <Section title="Recent comments" testid="csat-comments-section">
            {!asArray(report.recent_comments).length && (
              <EmptyRow msg="No survey comments in this window." />
            )}
            {!!asArray(report.recent_comments).length && (
              <div className="space-y-2">
                {report.recent_comments.map((c, idx) => (
                  <div key={c.ticket_id || idx}
                    data-testid={`csat-comment-${c.ticket_id || idx}`}
                    className="bg-zinc-950 border border-zinc-900 rounded-md p-4">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">
                      <span className="flex items-center gap-1 text-zinc-400">
                        {c.score} <Star size={10} className="text-zinc-600" />
                      </span>
                      <span>·</span>
                      <span>Ticket #{c.ticket_number ?? c.ticket_id}</span>
                    </div>
                    <div className="text-sm text-zinc-300 whitespace-pre-wrap break-words">
                      {c.comment}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="text-sm font-medium text-zinc-200">{title}</div>
      {children}
    </div>
  );
}

function EmptyRow({ msg }) {
  return <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-6 text-zinc-600 text-sm">{msg}</div>;
}

// A table that degrades to labeled cards on mobile rather than horizontal
// scroll -- the same shape TimeApproval.jsx uses.
function Table({ head, rows }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
      <table className="w-full text-sm hidden md:table">
        <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          <tr>{head.map((h, i) => <th key={i} className="text-left px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} data-testid={r.testid} className="border-b border-zinc-900 hover:bg-zinc-900/40">
              {r.cells.map((c, i) => <td key={i} className="px-4 py-2 text-zinc-300 align-middle">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="md:hidden divide-y divide-zinc-900">
        {rows.map((r) => (
          <div key={r.key} data-testid={r.testid ? `${r.testid}-card` : undefined} className="p-4 space-y-1.5">
            {r.cells.map((c, i) => (
              <div key={i} className="text-sm text-zinc-300">
                {head[i] && <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-600 mr-1.5">{head[i]}:</span>}
                {c}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
